import {
  chercherCompte,
  csvJournal,
  csvListingVentes,
  equilibre,
  journalVentes,
  listingVentes,
  nomFichierListing,
  nomFichierExport,
} from '@imp/shared/export-comptable';
import {
  COMPTES_GCN_DEFAUT,
  csvGcn,
  ecrituresGcn,
  nomFichierGcn,
} from '@imp/shared/export-gcn';
import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, FileCheck, Hash, Landmark } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';


import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { formatEuro } from '@/modules/catalogue/api';
import {
  attribuerCodesManquants,
  type Encodage,
  getReglagesExport,
  listClientsSansCode,
  listFacturesExportables,
  marquerExportees,
  type ReglagesExportComptable,
  setReglagesExport,
  telechargerCsv,
} from '@/modules/factures/export-comptable-api';

const jourIso = (d: Date): string =>
  `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const TAUX = ['5.5', '10', '20'] as const;
const NATURES: { id: string; label: string }[] = [
  { id: 'materiau', label: 'Matériaux' },
  { id: 'main_oeuvre', label: 'Main d’œuvre' },
  { id: 'sous_traitance', label: 'Sous-traitance' },
];

/** Un champ de compte, avec l'intitulé que le plan comptable lui connaît. */
function ChampCompte({
  id,
  label,
  valeur,
  plan,
  onChange,
  aide,
}: {
  id: string;
  label: string;
  valeur: string;
  plan: ReadonlyMap<string, string> | undefined;
  onChange: (v: string) => void;
  aide?: string;
}) {
  const trouve = plan && valeur.trim() ? chercherCompte(valeur, plan) : null;
  let note: { texte: string; teinte: string } | null = null;
  if (plan && valeur.trim()) {
    if (!trouve) note = { texte: 'absent du plan comptable — à confirmer avec le comptable', teinte: 'text-money-check' };
    else if (trouve.exact) note = { texte: trouve.libelle, teinte: 'text-muted-foreground' };
    else note = { texte: `sous-compte de ${trouve.code} « ${trouve.libelle} »`, teinte: 'text-muted-foreground' };
  }
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={valeur}
        inputMode="numeric"
        className="font-mono"
        onChange={(e) => {
          onChange(e.target.value.replace(/\s/g, ''));
        }}
      />
      {note ? <p className={`text-xs ${note.teinte}`}>{note.texte}</p> : null}
      {aide ? <p className="text-xs text-muted-foreground">{aide}</p> : null}
    </div>
  );
}

export function ExportComptablePage() {
  const queryClient = useQueryClient();
  const aujourdhui = new Date();
  // Le mois en cours : c'est la période qu'on exporte tous les mois pour le
  // comptable. L'année entière restait à portée de deux clics sur les dates.
  const premierDuMois = `${jourIso(aujourdhui).slice(0, 7)}-01`;
  const [debut, setDebut] = useState(premierDuMois);
  const [fin, setFin] = useState(jourIso(aujourdhui));
  const [seulementNouvelles, setSeulementNouvelles] = useState(true);
  const [periode, setPeriode] = useState<{ debut: string; fin: string } | null>(null);
  const [statut, setStatut] = useState<string | null>(null);
  const [brouillon, setBrouillon] = useState<ReglagesExportComptable | null>(null);

  const reglagesQ = useQuery({ queryKey: ['export-comptable-reglages'], queryFn: getReglagesExport });
  const planQ = useQuery({
    queryKey: ['plan-comptable'],
    queryFn: async () => new Map((await import('./plan-comptable')).PLAN_COMPTABLE),
    staleTime: Infinity,
  });
  const facturesQ = useQuery({
    queryKey: ['factures-exportables', periode],
    queryFn: () => listFacturesExportables(periode?.debut ?? '', periode?.fin ?? ''),
    enabled: periode !== null,
  });
  const sansCodeQ = useQuery({ queryKey: ['clients-sans-code'], queryFn: listClientsSansCode });
  const attribuerMut = useMutation({
    mutationFn: async () => {
      if (!reglagesQ.data) throw new Error('réglages non chargés');
      const ids = (sansCodeQ.data ?? []).map((c) => c.id);
      return attribuerCodesManquants(ids, reglagesQ.data);
    },
    onSuccess: (n) => {
      setStatut(`${String(n)} numéro(s) de compte attribué(s).`);
      void queryClient.invalidateQueries({ queryKey: ['clients-sans-code'] });
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (e: Error) => {
      setStatut(`Attribution impossible : ${e.message}`);
    },
  });

  const reglages = brouillon ?? reglagesQ.data ?? null;
  const modifier = (patch: Partial<ReglagesExportComptable>) => {
    if (!reglages) return;
    setBrouillon({ ...reglages, ...patch });
  };

  const saveMut = useMutation({
    mutationFn: (r: ReglagesExportComptable) => setReglagesExport(r),
    onSuccess: () => {
      setBrouillon(null);
      setStatut('Comptes enregistrés.');
      void queryClient.invalidateQueries({ queryKey: ['export-comptable-reglages'] });
    },
    onError: (e: Error) => {
      setStatut(`Erreur d'enregistrement : ${e.message}`);
    },
  });

  // Les pièces retenues : toutes celles de la période, ou seulement celles
  // qui ne sont jamais parties chez le comptable.
  const toutes = facturesQ.data ?? [];
  const retenues = seulementNouvelles ? toutes.filter((f) => f.exporteeDans === null) : toutes;
  const dejaParties = toutes.length - retenues.length;
  const resultat = reglages && retenues.length > 0 ? journalVentes(retenues, reglages) : null;
  const bilan = resultat ? equilibre(resultat.ecritures) : null;

  const exportMut = useMutation({
    mutationFn: async () => {
      if (!resultat || !reglages || !periode) throw new Error('rien à exporter');
      // Les clients sans numéro de compte en reçoivent un maintenant, puis on
      // relit les factures pour que les écritures portent le bon compte.
      let pieces = retenues;
      if (reglages.clientsIndividuels) {
        const ids = retenues.map((f) => f.contactId).filter((c): c is string => c !== null);
        const n = await attribuerCodesManquants(ids, reglages);
        if (n > 0) {
          const relues = await listFacturesExportables(periode.debut, periode.fin);
          const gardees = new Set(retenues.map((f) => f.id));
          pieces = relues.filter((f) => gardees.has(f.id));
          void queryClient.invalidateQueries({ queryKey: ['clients-sans-code'] });
        }
      }
      const journal = journalVentes(pieces, reglages);
      const eq = equilibre(journal.ecritures);
      if (eq.ecart !== 0)
        throw new Error(`les écritures ne s'équilibrent pas (écart ${formatEuro(eq.ecart)}) : export refusé`);
      const genereLe = jourIso(new Date());
      const nom = nomFichierExport(genereLe, periode.debut, periode.fin);
      telechargerCsv(nom, csvJournal(journal.ecritures), reglages.encodage);
      // Le détail des pièces part AVEC le journal : le comptable reçoit d'un
      // même geste ce que son logiciel avale et ce qu'un humain peut relire.
      const nomDetail = nomFichierListing(genereLe, periode.debut, periode.fin);
      telechargerCsv(nomDetail, csvListingVentes(listingVentes(pieces, reglages)), reglages.encodage);
      await marquerExportees(
        pieces.map((f) => f.id),
        nom,
      );
      return { nom, nomDetail, nb: pieces.length };
    },
    onSuccess: ({ nom, nomDetail, nb }) => {
      setStatut(
        `Deux fichiers téléchargés pour ${String(nb)} pièce(s) : ${nom} (journal) et ${nomDetail} (détail des pièces). Les pièces sont marquées comme exportées.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['factures-exportables'] });
    },
    onError: (e: Error) => {
      setStatut(`Export impossible : ${e.message}`);
    },
  });

  // LE FICHIER QUE LE CABINET IMPORTE VRAIMENT.
  //
  // Le journal ci-dessus suit un autre modèle Extrabat, qu'UNITIO a refusé en
  // septembre 2026 : « a priori ce n'est pas cet export là ». Ce bouton-ci
  // produit le format repris de leur propre fichier d'exemple.
  //
  // Il ne marque RIEN comme exporté et n'attribue aucun compte client : tant
  // que le cabinet n'a pas validé un premier fichier, on ne veut ni trace
  // d'export prématurée, ni comptes auxiliaires créés au jugé.
  const gcnMut = useMutation({
    mutationFn: () => {
      if (!periode) throw new Error('aucune période');
      if (retenues.length === 0) throw new Error('aucune pièce sur la période');
      const r = ecrituresGcn(retenues, COMPTES_GCN_DEFAUT);
      if (r.lignes.length === 0) {
        throw new Error(
          r.avertissements[0]?.message ?? 'aucune pièce exportable — voir les avertissements',
        );
      }
      const nom = nomFichierGcn(periode.debut, periode.fin);
      // UTF-8 sans BOM : c'est ce qu'est le fichier du cabinet, relu octet par
      // octet. Un BOM en tête ferait échouer la première ligne à l'import.
      telechargerCsv(nom, csvGcn(r, COMPTES_GCN_DEFAUT), 'utf-8');
      return Promise.resolve({ nom, nb: r.nbPieces, avertissements: r.avertissements });
    },
    onSuccess: ({ nom, nb, avertissements }) => {
      const reste =
        avertissements.length > 0
          ? ` ${String(avertissements.length)} pièce(s) écartée(s) : ${avertissements.map((a) => a.message).join(' · ')}`
          : '';
      setStatut(
        `${nom} téléchargé : ${String(nb)} pièce(s) au format du cabinet.${reste} Rien n'a été marqué comme exporté — faites valider ce fichier par UNITIO avant de vous en servir.`,
      );
    },
    onError: (e: Error) => {
      setStatut(`Export cabinet impossible : ${e.message}`);
    },
  });

  // Le listing détaillé : une ligne par ligne de facture, pour l'entreprise.
  // Il ne marque RIEN comme exporté — c'est une lecture, pas une remise au
  // comptable, et on doit pouvoir le retirer autant de fois qu'on veut.
  const listingMut = useMutation({
    mutationFn: () => {
      if (!reglages || !periode) throw new Error('rien à lister');
      if (retenues.length === 0) throw new Error('aucune pièce sur la période');
      const nom = nomFichierListing(jourIso(new Date()), periode.debut, periode.fin);
      telechargerCsv(nom, csvListingVentes(listingVentes(retenues, reglages)), reglages.encodage);
      return Promise.resolve({ nom, nb: retenues.length });
    },
    onSuccess: ({ nom, nb }) => {
      setStatut(`Listing ${nom} téléchargé : ${String(nb)} pièce(s) détaillée(s).`);
    },
    onError: (e: Error) => {
      setStatut(`Listing impossible : ${e.message}`);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="icon">
          <Link to="/factures" aria-label="Retour">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">Export comptable</h1>
          <p className="text-muted-foreground">
            Deux fichiers pour le comptable : le journal des ventes, que son logiciel avale, et le détail des pièces du mois, qu’un humain peut relire.</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/factures/reglements">
            <Landmark className="h-5 w-5" /> Règlements
          </Link>
        </Button>
      </div>

      {statut ? (
        <div className="rounded-md border border-border bg-card px-4 py-2 text-sm">{statut}</div>
      ) : null}

      {/* ----- Les comptes ----- */}
      <Card>
        <CardHeader>
          <CardTitle>Comptes utilisés</CardTitle>
          <p className="text-sm text-muted-foreground">
            Posés une fois avec le comptable. Les valeurs proposées sont celles de l&apos;export
            Extrabat qu&apos;il reçoit déjà ; l&apos;intitulé sous chaque champ vient de son plan
            comptable.
          </p>
        </CardHeader>
        <CardContent>
          {reglages ? (
            <div className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="journal">Code du journal</Label>
                  <Input
                    id="journal"
                    value={reglages.journal}
                    className="font-mono uppercase"
                    onChange={(e) => {
                      modifier({ journal: e.target.value.toUpperCase().trim() });
                    }}
                  />
                </div>
                <ChampCompte
                  id="ventes"
                  label="Compte de ventes"
                  valeur={reglages.ventes}
                  plan={planQ.data}
                  onChange={(v) => {
                    modifier({ ventes: v });
                  }}
                />
                <ChampCompte
                  id="client"
                  label="Compte client"
                  valeur={reglages.client}
                  plan={planQ.data}
                  onChange={(v) => {
                    modifier({ client: v });
                  }}
                />
              </div>

              <div>
                <p className="mb-2 text-sm font-medium">TVA collectée, par taux</p>
                <div className="grid gap-4 sm:grid-cols-4">
                  {TAUX.map((t) => (
                    <ChampCompte
                      key={t}
                      id={`tva-${t}`}
                      label={`${t.replace('.', ',')} %`}
                      valeur={reglages.tvaParTaux[t] ?? ''}
                      plan={planQ.data}
                      onChange={(v) => {
                        const tvaParTaux = Object.fromEntries(
                          Object.entries(reglages.tvaParTaux).filter(([k]) => k !== t),
                        );
                        if (v) tvaParTaux[t] = v;
                        modifier({ tvaParTaux });
                      }}
                    />
                  ))}
                  <ChampCompte
                    id="tva-defaut"
                    label="Autre taux"
                    valeur={reglages.tvaDefaut}
                    plan={planQ.data}
                    onChange={(v) => {
                      modifier({ tvaDefaut: v });
                    }}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Un taux sans compte prend « Autre taux ». Extrabat écrit tout sur 445710 ;
                  certains cabinets veulent un compte par taux.
                </p>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium">
                  Compte de ventes par nature d&apos;article{' '}
                  <span className="font-normal text-muted-foreground">
                    (vide = compte de ventes ci-dessus)
                  </span>
                </p>
                <div className="grid gap-4 sm:grid-cols-3">
                  {NATURES.map((n) => (
                    <ChampCompte
                      key={n.id}
                      id={`nature-${n.id}`}
                      label={n.label}
                      valeur={reglages.ventesParKind[n.id] ?? ''}
                      plan={planQ.data}
                      onChange={(v) => {
                        const ventesParKind = Object.fromEntries(
                          Object.entries(reglages.ventesParKind).filter(([k]) => k !== n.id),
                        );
                        if (v) ventesParKind[n.id] = v;
                        modifier({ ventesParKind });
                      }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium">Comptes clients</p>
                <label className="mb-3 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-5 w-5 accent-primary"
                    checked={reglages.clientsIndividuels}
                    onChange={(e) => {
                      modifier({ clientsIndividuels: e.target.checked });
                    }}
                  />
                  Un compte par client (préfixe + numéro), plutôt que le collectif ci-dessus
                </label>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="prefixe-client">Préfixe</Label>
                    <Input
                      id="prefixe-client"
                      value={reglages.prefixeClient}
                      className="font-mono uppercase"
                      onChange={(e) => {
                        modifier({ prefixeClient: e.target.value.toUpperCase().replace(/\s/g, '') });
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="chiffres-client">Chiffres après le préfixe</Label>
                    <Input
                      id="chiffres-client"
                      inputMode="numeric"
                      value={String(reglages.chiffresClient)}
                      onChange={(e) => {
                        const n = Number(e.target.value.replace(/\D/g, ''));
                        modifier({ chiffresClient: Math.min(Math.max(n, 1), 10) });
                      }}
                    />
                    <p className="text-xs text-muted-foreground">
                      Exemple : {reglages.prefixeClient}
                      {'1'.padStart(reglages.chiffresClient, '0')}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Clients sans numéro</Label>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm tabular-nums">
                        {sansCodeQ.data ? sansCodeQ.data.length : '…'}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!sansCodeQ.data || sansCodeQ.data.length === 0 || attribuerMut.isPending || brouillon !== null}
                        onClick={() => {
                          attribuerMut.mutate();
                        }}
                      >
                        <Hash className="h-4 w-4" /> Attribuer les numéros
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Sinon, c&apos;est fait tout seul au premier export de chaque client.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium">Règlements</p>
                <div className="grid gap-4 sm:grid-cols-4">
                  <ChampCompte
                    id="banque"
                    label="Compte banque"
                    valeur={reglages.banque}
                    plan={planQ.data}
                    onChange={(v) => {
                      modifier({ banque: v });
                    }}
                  />
                  <div className="space-y-1.5">
                    <Label htmlFor="journal-banque">Journal de banque</Label>
                    <Input
                      id="journal-banque"
                      value={reglages.journalBanque}
                      className="font-mono uppercase"
                      onChange={(e) => {
                        modifier({ journalBanque: e.target.value.toUpperCase().trim() });
                      }}
                    />
                  </div>
                  <ChampCompte
                    id="caisse"
                    label="Compte caisse (espèces)"
                    valeur={reglages.caisse}
                    plan={planQ.data}
                    onChange={(v) => {
                      modifier({ caisse: v });
                    }}
                  />
                  <div className="space-y-1.5">
                    <Label htmlFor="journal-caisse">Journal de caisse</Label>
                    <Input
                      id="journal-caisse"
                      value={reglages.journalCaisse}
                      className="font-mono uppercase"
                      onChange={(e) => {
                        modifier({ journalCaisse: e.target.value.toUpperCase().trim() });
                      }}
                    />
                  </div>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Les règlements s&apos;exportent depuis la page{' '}
                  <Link to="/factures/reglements" className="text-primary hover:underline">
                    Règlements
                  </Link>
                  , avec ces comptes.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="encodage">Encodage du fichier</Label>
                  <Select
                    id="encodage"
                    value={reglages.encodage}
                    onChange={(e) => {
                      modifier({ encodage: e.target.value as Encodage });
                    }}
                  >
                    <option value="windows-1252">Windows (ANSI) — la plupart des logiciels comptables</option>
                    <option value="utf-8">UTF-8</option>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Si les accents des noms de clients arrivent abîmés chez le comptable, changez ici.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={brouillon === null || saveMut.isPending}
                  onClick={() => {
                    if (brouillon) saveMut.mutate(brouillon);
                  }}
                >
                  <FileCheck className="h-5 w-5" /> Enregistrer les comptes
                </Button>
                {brouillon ? (
                  <span className="text-sm text-muted-foreground">Modifications non enregistrées.</span>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Chargement des réglages…</p>
          )}
        </CardContent>
      </Card>

      {/* ----- L'export ----- */}
      <Card>
        <CardHeader>
          <CardTitle>Préparer un export</CardTitle>
          <p className="text-sm text-muted-foreground">
            Seules les factures et avoirs émis sont exportés — jamais un brouillon. Chaque pièce
            exportée garde la trace du fichier dans lequel elle est partie.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="debut">Du</Label>
              <Input
                id="debut"
                type="date"
                value={debut}
                className="w-44"
                onChange={(e) => {
                  setDebut(e.target.value);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fin">Au</Label>
              <Input
                id="fin"
                type="date"
                value={fin}
                className="w-44"
                onChange={(e) => {
                  setFin(e.target.value);
                }}
              />
            </div>
            <label className="flex h-12 items-center gap-2 text-sm lg:h-11">
              <input
                type="checkbox"
                className="h-5 w-5 accent-primary"
                checked={seulementNouvelles}
                onChange={(e) => {
                  setSeulementNouvelles(e.target.checked);
                }}
              />
              Uniquement les pièces jamais exportées
            </label>
            <Button
              type="button"
              variant="outline"
              disabled={!debut || !fin || debut > fin}
              onClick={() => {
                setStatut(null);
                setPeriode({ debut, fin });
              }}
            >
              Préparer
            </Button>
          </div>

          {periode ? (
            facturesQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Lecture des factures…</p>
            ) : facturesQ.isError ? (
              <p className="text-sm text-destructive">Erreur : {messageErreur(facturesQ.error)}</p>
            ) : toutes.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Aucune facture émise entre le {periode.debut} et le {periode.fin}.
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-medium">Pièce</th>
                        <th className="px-3 py-2 font-medium">Date</th>
                        <th className="px-3 py-2 font-medium">Client</th>
                        <th className="px-3 py-2 font-medium">Compte</th>
                        <th className="px-3 py-2 text-right font-medium">HT</th>
                        <th className="px-3 py-2 text-right font-medium">TVA</th>
                        <th className="px-3 py-2 text-right font-medium">TTC</th>
                        <th className="px-3 py-2 font-medium">Déjà exportée</th>
                      </tr>
                    </thead>
                    <tbody>
                      {toutes.map((f) => {
                        const ecartee = seulementNouvelles && f.exporteeDans !== null;
                        const signe = f.kind === 'avoir' ? -1 : 1;
                        return (
                          <tr
                            key={f.id}
                            className={`border-b last:border-0 ${ecartee ? 'text-muted-foreground' : ''}`}
                          >
                            <td className="px-3 py-2 font-mono text-xs">
                              {f.numero}
                              {f.kind === 'avoir' ? (
                                <span className="ml-1 rounded bg-money-out-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase text-money-out">
                                  avoir
                                </span>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 tabular-nums">{f.dateEmission}</td>
                            <td className="px-3 py-2">{f.client}</td>
                            <td className="px-3 py-2 font-mono text-xs">
                              {f.compteClient ?? (
                                <span className="text-muted-foreground">
                                  {reglages?.clientsIndividuels ? 'attribué à l’export' : reglages?.client}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatEuro(signe * f.montantHt)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatEuro(signe * f.montantTva)}</td>
                            <td className="px-3 py-2 text-right font-semibold tabular-nums">
                              {formatEuro(signe * Math.abs(f.montantTtc))}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {f.exporteeDans ? (
                                <span title={f.exporteeDans}>
                                  {f.exporteeLe ? new Date(f.exporteeLe).toLocaleDateString('fr-FR') : 'oui'}
                                  {ecartee ? ' — écartée' : ' — réexportée'}
                                </span>
                              ) : (
                                <span className="text-money-in">jamais</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {resultat && bilan ? (
                  <div className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-4">
                      <div className="rounded-md border px-3 py-2">
                        <div className="text-xs uppercase text-muted-foreground">Pièces</div>
                        <div className="text-xl font-bold tabular-nums">{resultat.nbPieces}</div>
                        {dejaParties > 0 ? (
                          <div className="text-xs text-muted-foreground">
                            {dejaParties} déjà exportée(s), écartée(s)
                          </div>
                        ) : null}
                      </div>
                      <div className="rounded-md border px-3 py-2">
                        <div className="text-xs uppercase text-muted-foreground">Total HT</div>
                        <div className="text-xl font-bold tabular-nums">{formatEuro(resultat.totalHt)}</div>
                      </div>
                      <div className="rounded-md border px-3 py-2">
                        <div className="text-xs uppercase text-muted-foreground">Total TVA</div>
                        <div className="text-xl font-bold tabular-nums">{formatEuro(resultat.totalTva)}</div>
                      </div>
                      <div
                        className={`rounded-md border px-3 py-2 ${bilan.ecart === 0 ? 'border-money-in' : 'border-money-out bg-money-out-soft'}`}
                      >
                        <div className="text-xs uppercase text-muted-foreground">Équilibre</div>
                        <div className={`text-xl font-bold tabular-nums ${bilan.ecart === 0 ? 'text-money-in' : 'text-money-out'}`}>
                          {bilan.ecart === 0 ? 'débit = crédit' : `écart ${formatEuro(bilan.ecart)}`}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {resultat.ecritures.length} écritures · {formatEuro(bilan.debit)}
                        </div>
                      </div>
                    </div>

                    {resultat.avertissements.length > 0 ? (
                      <div className="rounded-md border border-money-check bg-money-check-soft px-4 py-3 text-sm text-money-check">
                        <p className="font-semibold">
                          {resultat.avertissements.length} point(s) à regarder avant d&apos;envoyer :
                        </p>
                        <ul className="mt-1 list-disc space-y-0.5 pl-5">
                          {resultat.avertissements.map((a, i) => (
                            <li key={`${a.piece}-${String(i)}`}>
                              <span className="font-mono">{a.piece}</span> — {a.message}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    <Button
                      type="button"
                      disabled={exportMut.isPending || bilan.ecart !== 0 || brouillon !== null}
                      onClick={() => {
                        exportMut.mutate();
                      }}
                    >
                      <Download className="h-5 w-5" /> Télécharger pour le comptable (
                      {resultat.nbPieces} pièce{resultat.nbPieces > 1 ? 's' : ''})
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={gcnMut.isPending}
                      onClick={() => {
                        gcnMut.mutate();
                      }}
                    >
                      <Download className="h-5 w-5" /> Format UNITIO (écritures)
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={listingMut.isPending}
                      onClick={() => {
                        listingMut.mutate();
                      }}
                    >
                      <Download className="h-5 w-5" /> Le détail seul
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Le bouton du comptable sort <strong>deux fichiers</strong> : le journal des
                      ventes, que son logiciel avale, et le détail des pièces du mois — une ligne par
                      ligne de facture, avec sa désignation et son taux. Le second bouton reprend ce
                      détail seul, sans rien marquer comme exporté : autant de fois qu&apos;on veut.
                    </p>
                    <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      <strong>Format UNITIO</strong> : le fichier d&apos;écritures repris du modèle
                      envoyé par le cabinet le 19/09/2026, avec ses comptes (20 % · 10 % · 5,5 %) et
                      ses fiches clients. Il ne marque rien comme exporté et n&apos;attribue aucun
                      compte client — <strong>faites-en valider un par Samantha</strong> avant de
                      vous en servir pour de bon. Un client sans compte comptable au format{' '}
                      <code>01XXXXXXXX</code> est écarté du fichier, avec sa proposition affichée.
                    </p>
                    {brouillon !== null ? (
                      <p className="text-xs text-money-check">
                        Enregistrez d&apos;abord les comptes modifiés.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Toutes les pièces de la période ont déjà été exportées. Décochez la case pour les
                    réexporter.
                  </p>
                )}
              </>
            )
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
